import { useEffect, useState } from 'react';
import { io, type Socket } from 'socket.io-client';

/**
 * Lazily-initialised Socket.IO client. Connects to the same origin as the
 * SPA — Vite proxies /socket.io to the Fastify api in dev, nginx in prod.
 * Cookies (lsync_session) are sent automatically so server-side auth runs.
 *
 * Connection-status observability — `useSocketStatus()` hook below — lets
 * the chat page render a "Reconnecting…" pill when the handshake fails so
 * the user knows they're seeing stale data instead of silently wondering
 * why new messages don't appear.
 */
let socket: Socket | null = null;

export type SocketStatus = 'connecting' | 'connected' | 'disconnected' | 'forbidden';

const statusListeners = new Set<(s: SocketStatus) => void>();
let currentStatus: SocketStatus = 'connecting';

function setStatus(next: SocketStatus): void {
  if (next === currentStatus) return;
  currentStatus = next;
  for (const fn of statusListeners) fn(next);
}

export function getSocket(): Socket {
  if (socket) return socket;
  socket = io({
    path: '/socket.io',
    autoConnect: true,
    withCredentials: true,
    transports: ['polling', 'websocket'],
    // The default retry is fine for transient failures; reconnection is
    // automatic with exponential backoff between 1s and 5s.
    reconnection: true,
    reconnectionDelayMax: 5000,
  });

  socket.on('connect', () => {
    setStatus('connected');
  });
  socket.on('disconnect', (reason) => {
    // 'io server disconnect' means the server explicitly closed us (auth
    // failure post-connect). Don't retry; surface as forbidden so the UI
    // can prompt the user to refresh / re-login.
    if (reason === 'io server disconnect') {
      setStatus('forbidden');
      return;
    }
    setStatus('disconnected');
  });
  socket.on('connect_error', (err) => {
    // `forbidden` is what our auth middleware sends; anything else is a
    // transport / network failure that Socket.IO will retry automatically.
    if (err && (err.message === 'forbidden' || err.message === 'unauthorized')) {
      setStatus('forbidden');
    } else {
      setStatus('disconnected');
    }
    // eslint-disable-next-line no-console
    console.warn('[socket] connect_error', err?.message ?? err);
  });

  return socket;
}

/**
 * Returns the current Socket.IO connection status. Components that render
 * realtime-dependent UI can use this to show a "Reconnecting…" affordance
 * so users know when they're looking at potentially-stale data.
 */
export function useSocketStatus(): SocketStatus {
  const [status, setLocalStatus] = useState<SocketStatus>(currentStatus);

  useEffect(() => {
    // Eagerly construct the socket so the status reflects the real attempt.
    getSocket();
    statusListeners.add(setLocalStatus);
    setLocalStatus(currentStatus);
    return () => {
      statusListeners.delete(setLocalStatus);
    };
  }, []);

  return status;
}
